import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCurrentlyLoggedInUser } from '../../CommonFunctions';
import config from '../../config';
import DashboardHeader from './SharedDashboardViews/DashboardHeader';
import DailySalesChart from './SharedDashboardViews/DailySalesChart';
import ActivitySummary from './SharedDashboardViews/ActivitySummary';
import FinancialBreakdown from './SharedDashboardViews/FinancialBreakdown';
import TopSellers from './SharedDashboardViews/TopSellers';
import TopDivisions from './SharedDashboardViews/TopDivisions';
import ParticipantSalesTable from './SharedDashboardViews/ParticipantSalesTable';
import { CoordinatorDashboardData, DivisionInfo, DivisionChartData } from './SharedDashboardViews/types';

type RouteParams = Record<string, string | undefined>;

const BRAND = '#163172';

const fetchWithAuth = async (url: string) => {
  const token = localStorage.getItem('accessToken');
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return res.json();
};

// Skeleton matches the real layout precisely to eliminate layout shift
const SkeletonCard: FC<{ h?: number }> = ({ h = 120 }) => (
  <div className="rounded-3" style={{ height: h, background: 'linear-gradient(90deg,#e8eaf6 25%,#dde2f5 50%,#e8eaf6 75%)', animation: 'pulse 1.5s infinite' }} />
);

const CoordinatorSkeleton: FC = () => (
  <div className="p-2 p-md-3" style={{ background: '#f8f9fb', minHeight: '100vh' }}>
    <SkeletonCard h={140} />
    <div className="my-2" style={{ height: 32, background: 'linear-gradient(90deg,#e8eaf6 25%,#dde2f5 50%,#e8eaf6 75%)', borderRadius: 20, maxWidth: 480 }} />
    <div className="row g-3 mb-3">
      <div className="col-8"><SkeletonCard h={170} /></div>
      <div className="col-4"><SkeletonCard h={170} /></div>
    </div>
    <div className="row g-3 mb-3">
      <div className="col-6"><SkeletonCard h={160} /></div>
      <div className="col-6"><SkeletonCard h={160} /></div>
    </div>
    <SkeletonCard h={260} />
    <div className="row g-3 mt-2">
      <div className="col-6"><SkeletonCard h={120} /></div>
      <div className="col-6"><SkeletonCard h={120} /></div>
    </div>
  </div>
);

const ChartLoadingCard: FC<{ label?: string }> = ({ label = 'Loading chart…' }) => (
  <div
    className="bg-white rounded-3 shadow-sm p-4 h-100 d-flex align-items-center justify-content-center"
    style={{ border: '1px solid #f0f0f0', minHeight: 160 }}
  >
    <div className="text-center">
      <div className="spinner-border spinner-border-sm text-secondary mb-2" role="status" aria-hidden="true" />
      <p className="text-muted mb-0" style={{ fontSize: 12 }}>{label}</p>
    </div>
  </div>
);

const ChartUnavailablePlaceholder: FC<{ icon?: string; title?: string; subtitle?: string }> = ({
  icon = '📊',
  title = 'Chart unavailable for this team',
  subtitle = 'Select "All Participants" to see the 7-day fundraiser trend.',
}) => (
  <div
    className="bg-white rounded-3 shadow-sm p-4 h-100 d-flex flex-column align-items-center justify-content-center text-center"
    style={{ border: '1px dashed #e2e8f0', minHeight: 160 }}
  >
    <div style={{ fontSize: 28, opacity: 0.3, marginBottom: 8 }}>{icon}</div>
    <p className="fw-semibold mb-1" style={{ fontSize: 13, color: '#475569' }}>
      {title}
    </p>
    <p className="text-muted mb-0" style={{ fontSize: 12 }}>
      {subtitle}
    </p>
  </div>
);

// Empty state when the selected division has no participants yet
const NoParticipantsInTeam: FC<{ divisionName: string }> = ({ divisionName }) => (
  <div
    className="bg-white rounded-3 shadow-sm p-5 mb-4 text-center"
    style={{ border: '1px solid #f0f0f0' }}
  >
    <div style={{ fontSize: 40, opacity: 0.25, marginBottom: 12 }}>👥</div>
    <h6 className="fw-bold mb-2" style={{ color: '#1a1a2e' }}>No participants in {divisionName} yet</h6>
    <p className="text-muted mb-0" style={{ fontSize: 13, maxWidth: 360, margin: '0 auto' }}>
      Share the invite link with families so they can register under this team and start fundraising.
    </p>
  </div>
);

const CoordinatorDashboard: FC = () => {
  const { linkExtension } = useParams<RouteParams>();
  const fundraiserId = linkExtension ? parseInt(linkExtension, 10) : 0;
  const navigate = useNavigate();

  const [accountId, setAccountId] = useState<number | null>(null);
  const [dashboardData, setDashboardData] = useState<CoordinatorDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Division filter state
  const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>(null);

  // Division-scoped chart data (fetched separately when a division is selected)
  const [divisionChart, setDivisionChart] = useState<DivisionChartData | null>(null);
  const [divisionChartLoading, setDivisionChartLoading] = useState(false);

  useEffect(() => {
    document.title = 'Coordinator Dashboard';
    getCurrentlyLoggedInUser()
      .then(user => setAccountId(user.id))
      .catch(() => navigate('/login'));
  }, [navigate]);

  const fetchDashboard = useCallback(async () => {
    if (!accountId || !fundraiserId) return;
    try {
      setLoading(true);
      const data: CoordinatorDashboardData = await fetchWithAuth(
        `${config.API_BASE_URL}dashboards/coordinator/?coordinator_account_id=${accountId}&fundraiser_id=${fundraiserId}`
      );
      setDashboardData(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [accountId, fundraiserId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Fetch division-scoped chart whenever the selected division changes
  useEffect(() => {
    if (selectedDivisionId === null || !accountId || !fundraiserId) {
      setDivisionChart(null);
      return;
    }
    setDivisionChartLoading(true);
    fetchWithAuth(
      `${config.API_BASE_URL}dashboards/coordinator/division-chart/?coordinator_account_id=${accountId}&fundraiser_id=${fundraiserId}&secondary_division_id=${selectedDivisionId}`
    )
      .then((data: DivisionChartData) => {
        setDivisionChart(data);
        setDivisionChartLoading(false);
      })
      .catch(() => {
        setDivisionChart(null);
        setDivisionChartLoading(false);
      });
  }, [selectedDivisionId, accountId, fundraiserId]);

  // Client-side filtered rows
  const filteredRows = useMemo(() => {
    if (!dashboardData) return [];
    if (selectedDivisionId === null) return dashboardData.participant_sales_table;
    return dashboardData.participant_sales_table.filter(
      r => r.secondary_div_id === selectedDivisionId
    );
  }, [dashboardData, selectedDivisionId]);

  const filteredStats = useMemo(() => {
    if (!dashboardData || selectedDivisionId === null) return null;
    return {
      total_raised: filteredRows.reduce((s, r) => s + r.total_sales, 0),
      total_profit: filteredRows.reduce((s, r) => s + r.total_profit, 0),
      participant_count: filteredRows.length,
    };
  }, [dashboardData, selectedDivisionId, filteredRows]);

  const selectedDivision: DivisionInfo | undefined = dashboardData?.divisions.find(
    d => d.secondary_id === selectedDivisionId
  );

  if (loading) return <CoordinatorSkeleton />;

  if (error) {
    return (
      <div className="d-flex align-items-center justify-content-center p-5" style={{ minHeight: '60vh', background: '#f8f9fb' }}>
        <div className="bg-white rounded-3 shadow-sm p-5 text-center" style={{ maxWidth: 420 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <h5 className="fw-bold mb-2">Failed to load dashboard</h5>
          <p className="text-muted small mb-4">{error}</p>
          <button onClick={fetchDashboard} className="btn btn-sm fw-semibold text-white px-4" style={{ background: BRAND, border: 'none' }}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) return null;

  const hasDivisions = dashboardData.divisions.length > 0;
  const isFiltered   = selectedDivisionId !== null;
  const manyDivisions = dashboardData.divisions.length > 6;

  const activeCount = dashboardData.participant_active_summary.active +
    dashboardData.participant_active_summary.yesterday +
    dashboardData.participant_active_summary.last_week;

  const displayRaised  = isFiltered && filteredStats ? filteredStats.total_raised  : dashboardData.total_sales_amount;
  const displayProfit  = isFiltered && filteredStats ? filteredStats.total_profit  : dashboardData.total_profit;
  const displayCount   = filteredRows.length;

  // Determine which chart data to render in the left chart slot
  const renderChartSlot = () => {
    if (!isFiltered) return <DailySalesChart data={dashboardData.last_seven_daily_sales} />;
    if (divisionChartLoading) return <ChartLoadingCard />;
    if (divisionChart?.last_seven_daily_sales) return <DailySalesChart data={divisionChart.last_seven_daily_sales} />;
    return <ChartUnavailablePlaceholder />;
  };

  const renderActivitySlot = () => {
    if (!isFiltered) return <ActivitySummary data={dashboardData.participant_active_summary} />;
    if (divisionChartLoading) return <ChartLoadingCard label="Loading activity…" />;
    if (divisionChart?.participant_active_summary) return <ActivitySummary data={divisionChart.participant_active_summary} />;
    return (
      <ChartUnavailablePlaceholder
        icon="👥"
        title="Activity unavailable for this team"
        subtitle='Select "All Participants" to see overall activity.'
      />
    );
  };

  return (
    <div className="p-2 p-md-3" style={{ background: '#f8f9fb', minHeight: '100vh' }}>

      {/* Hero */}
      <DashboardHeader
        fundraiserName={dashboardData.fundraiser_name}
        fundraiserCode={dashboardData.fundraiser_code}
        scope={isFiltered && selectedDivision ? selectedDivision.secondary_name : 'All teams'}
        stats={{
          total_raised: displayRaised,
          participant_count: displayCount,
          total_orders: filteredRows.reduce((s, r) => s + r.num_items_sold, 0),
          days_remaining: dashboardData.days_remaining,
        }}
        role="Coordinator"
        lastUpdated={lastUpdated}
        onRefresh={fetchDashboard}
      />

      {/* Division filter pills */}
      {hasDivisions && (
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="fw-semibold text-muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Filter by team
            </span>
            <button
              onClick={() => navigate('/create-fundraiser/division-info', {
                state: {
                  fundraiserID: dashboardData.fundraiser_id,
                  teamAdminName: '',
                  primaryDivisionName: '',
                  secondaryDivisionName: '',
                },
              })}
              className="btn btn-sm ms-auto fw-semibold"
              style={{ fontSize: 12, padding: '6px 14px', border: `1px solid ${BRAND}`, color: BRAND, background: 'transparent', minHeight: 36 }}
            >
              ✏️ Edit Divisions
            </button>
          </div>

          {/* Horizontal scroll for > 6 teams; wrap otherwise */}
          <div
            style={manyDivisions
              ? { display: 'flex', overflowX: 'auto', gap: 8, whiteSpace: 'nowrap', paddingBottom: 4, WebkitOverflowScrolling: 'touch' }
              : { display: 'flex', flexWrap: 'wrap', gap: 8 }
            }
          >
            <button
              onClick={() => setSelectedDivisionId(null)}
              className="btn btn-sm fw-semibold flex-shrink-0"
              style={{
                fontSize: 12, padding: '6px 14px', borderRadius: 20,
                border: `1.5px solid ${selectedDivisionId === null ? BRAND : '#e2e8f0'}`,
                background: selectedDivisionId === null ? BRAND : '#fff',
                color: selectedDivisionId === null ? '#fff' : '#475569',
              }}
            >
              All Participants
              <span className="ms-1 opacity-75" style={{ fontSize: 10 }}>
                ({dashboardData.participant_sales_table.length})
              </span>
            </button>
            {dashboardData.divisions.map(div => (
              <button
                key={div.secondary_id}
                onClick={() => setSelectedDivisionId(div.secondary_id)}
                className="btn btn-sm fw-semibold flex-shrink-0"
                style={{
                  fontSize: 12, padding: '6px 14px', borderRadius: 20,
                  border: `1.5px solid ${selectedDivisionId === div.secondary_id ? BRAND : '#e2e8f0'}`,
                  background: selectedDivisionId === div.secondary_id ? BRAND : '#fff',
                  color: selectedDivisionId === div.secondary_id ? '#fff' : '#475569',
                }}
                title={[div.primary_name, div.team_admin_name ? `Team Admin: ${div.team_admin_name}` : null].filter(Boolean).join(' — ') || undefined}
              >
                {div.primary_name && (
                  <span
                    className="me-1"
                    style={{ fontSize: 9, opacity: 0.75, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                  >
                    {div.primary_name}
                  </span>
                )}
                {div.secondary_name}
                {div.team_admin_name && (
                  <span className="ms-1 opacity-75" style={{ fontSize: 10 }}>· {div.team_admin_name.split(' ')[0]}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Charts row — left slot shows all-fundraiser or division-scoped chart */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-8">
          {renderChartSlot()}
        </div>
        <div className="col-12 col-md-4">
          {renderActivitySlot()}
        </div>
      </div>

      {/* Top performers — only when viewing all participants */}
      {!isFiltered && (
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <TopSellers sellers={dashboardData.top_5_sellers} title="Top 5 Sellers" />
          </div>
          <div className="col-12 col-md-6">
            <TopDivisions divisions={dashboardData.top_5_secondary_divisions} />
          </div>
        </div>
      )}

      {/* Division info card — team admin contact when a division is selected */}
      {isFiltered && selectedDivision && (
        <div className="bg-white rounded-3 shadow-sm p-3 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-3" style={{ border: '1px solid #f0f0f0' }}>
          <div>
            <h6 className="fw-bold mb-1" style={{ color: '#1a1a2e' }}>{selectedDivision.secondary_name}</h6>
            {selectedDivision.primary_name && (
              <p className="text-muted mb-0" style={{ fontSize: 12 }}>{selectedDivision.primary_name}</p>
            )}
          </div>
          {selectedDivision.team_admin_name && (
            <div className="text-end">
              <p className="text-muted mb-0" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Team Admin</p>
              <p className="fw-semibold mb-0" style={{ fontSize: 14, color: '#1a1a2e' }}>{selectedDivision.team_admin_name}</p>
              {selectedDivision.team_admin_email && (
                <p className="text-muted mb-0" style={{ fontSize: 11 }}>{selectedDivision.team_admin_email}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Participant table — or helpful empty state when the filtered division is empty */}
      {isFiltered && filteredRows.length === 0 ? (
        <NoParticipantsInTeam divisionName={selectedDivision?.secondary_name ?? 'this team'} />
      ) : (
        <div className="bg-white rounded-3 shadow-sm p-3 mb-3" style={{ border: '1px solid #f0f0f0' }}>
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h6 className="fw-bold mb-0" style={{ color: '#1a1a2e', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {isFiltered ? `Participants — ${selectedDivision?.secondary_name ?? ''}` : 'All Participants'}
            </h6>
            <div className="d-flex gap-2">
              <span className="badge rounded-pill fw-normal" style={{ background: '#f0f3ff', color: BRAND, fontSize: 11 }}>
                {filteredRows.length} total
              </span>
              {!isFiltered && dashboardData.active_participants_percentage > 0 && (
                <span className="badge rounded-pill fw-normal" style={{ background: '#dcfce7', color: '#15803d', fontSize: 11 }}>
                  {Math.round(dashboardData.active_participants_percentage)}% active
                </span>
              )}
            </div>
          </div>
          <ParticipantSalesTable
            rows={filteredRows}
            showDivision={!isFiltered}
            groupByTeamAdmin={!isFiltered}
            fundraiserId={fundraiserId}
          />
        </div>
      )}

      {/* Financials — two col-md-6 cards fill the full row */}
      <div className="row g-3">
        <div className="col-12 col-md-6">
          <FinancialBreakdown data={{ total_raised: displayRaised, total_profit: displayProfit }} />
        </div>
        <div className="col-12 col-md-6">
          <div className="bg-white rounded-3 shadow-sm p-3 h-100" style={{ border: '1px solid #f0f0f0' }}>
            <h6 className="fw-bold mb-3" style={{ color: '#1a1a2e', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {isFiltered ? 'Team Summary' : '7-Day Summary'}
            </h6>
            {isFiltered ? (
              <div className="d-flex flex-column gap-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: 12, color: '#475569' }}>Participants in team</span>
                  <span className="fw-bold" style={{ fontSize: 14, color: BRAND }}>{filteredRows.length}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Items sold</span>
                  <span className="fw-bold" style={{ fontSize: 14, color: '#475569' }}>
                    {filteredRows.reduce((s, r) => s + r.num_items_sold, 0)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Avg raised per participant</span>
                  <span className="fw-bold" style={{ fontSize: 14, color: BRAND }}>
                    {filteredRows.length > 0
                      ? `$${(displayRaised / filteredRows.length).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                      : '$0'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ fontSize: 12, color: '#475569' }}>Revenue (7 days)</span>
                  <span className="fw-bold" style={{ fontSize: 14, color: BRAND }}>
                    ${Number(dashboardData.last_seven_days_sales).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Active this week</span>
                  <span className="fw-bold" style={{ fontSize: 14, color: '#059669' }}>{activeCount}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Total teams</span>
                  <span className="fw-bold" style={{ fontSize: 14, color: '#475569' }}>{dashboardData.divisions.length}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoordinatorDashboard;
